import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQueryClient, useQuery } from 'react-query';
import api from '../services/api.ts';
import FileUpload, { FileAttachment } from '../components/FileUpload.tsx';
import { formatCurrencyInput, parseCurrency, applyCurrencyMask, formatCurrency } from '../utils/currency.ts';

// Função utilitária para construir URLs do backend
const getBackendUrl = (path: string) => {
  const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
  return `${baseUrl}${path}`;
};
import FormPage from '../components/ui/FormPage.tsx';
import {
  ArrowLeftIcon,
  BuildingOfficeIcon,
  TruckIcon,
  CubeIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';

// Types
interface AssetFormData {
  description: string;
  type: 'real_estate' | 'vehicle' | 'other';
  acquisitionValue: number;
  amountPaid: number;
  currentValue: number;
  acquisitionDate: string;
  attachments: FileAttachment[];
  details: {
    // Propriedades para imóveis
    address?: string;
    area?: number;
    rooms?: number;
    bathrooms?: number;
    garage?: boolean;
    furnished?: boolean;
    registrationNumber?: string;
    // Propriedades para veículos
    make?: string;
    model?: string;
    year?: number;
    licensePlate?: string;
    color?: string;
    // Propriedades gerais
    notes?: string;
  };
}

const EditAsset: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState<AssetFormData>({
    description: '',
    type: 'real_estate',
    acquisitionValue: 0,
    amountPaid: 0,
    currentValue: 0,
    acquisitionDate: new Date().toISOString().split('T')[0],
    attachments: [],
    details: {},
  });

  // Estados para os valores formatados dos inputs
  const [acquisitionValueDisplay, setAcquisitionValueDisplay] = useState<string>('');
  const [amountPaidDisplay, setAmountPaidDisplay] = useState<string>('');
  const [currentValueDisplay, setCurrentValueDisplay] = useState<string>('');

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Query para buscar os dados do ativo
  const { data: asset, isLoading: isLoadingAsset } = useQuery(
    ['asset', id],
    async () => {
      const response = await api.get(`/assets/${id}`);
      return response.data;
    },
    {
      enabled: !!id,
      refetchOnWindowFocus: false, // Evitar refetch automático ao focar janela
      staleTime: 1000, // Reduzir para 1 segundo para forçar carregamento
      cacheTime: 5 * 60 * 1000, // 5 minutos - manter em cache
      onSuccess: data => {
        console.log('Query onSuccess executado com dados:', data);
        // Sempre atualizar os dados quando a query for bem-sucedida
        // Converter attachments do backend para o formato esperado pelo FileUpload
        const convertedAttachments: FileAttachment[] = (data.attachments || []).map(
          (attachment: any) => ({
            id: attachment.id || `existing-${Date.now()}-${Math.random()}`,
            name: attachment.name || attachment.originalName || 'Arquivo',
            type: attachment.mimetype?.startsWith('image/') ? 'image' : 'document',
            category: attachment.category || 'outros',
            file: null, // Arquivo já existe no servidor
            preview: attachment.mimetype?.startsWith('image/')
              ? getBackendUrl(attachment.url)
              : undefined,
            url: attachment.url ? getBackendUrl(attachment.url) : undefined,
            size: attachment.size || 0,
          })
        );

        setFormData({
          description: data.description || '',
          type: data.type || 'real_estate',
          acquisitionValue: data.acquisitionValue || 0,
          amountPaid: data.amountPaid || 0,
          currentValue: data.currentValue || 0,
          acquisitionDate: data.acquisitionDate
            ? data.acquisitionDate.split('T')[0]
            : new Date().toISOString().split('T')[0],
          attachments: convertedAttachments,
          details: data.details || {},
        });

        // Definir valores formatados
        setAcquisitionValueDisplay(formatCurrencyInput(data.acquisitionValue || 0));
        setAmountPaidDisplay(formatCurrencyInput(data.amountPaid || 0));
        setCurrentValueDisplay(formatCurrencyInput(data.currentValue || 0));
      },
    }
  );

  // Mutation para atualizar ativo
  const updateAssetMutation = useMutation(
    async (data: AssetFormData) => {
      let uploadedFiles: any[] = [];

      // Primeiro, fazer upload dos arquivos novos se existirem
      const newAttachments = data.attachments.filter(att => att.file instanceof File);
      if (newAttachments.length > 0) {
        const formData = new FormData();
        newAttachments.forEach((attachment: any) => {
          formData.append('files', attachment.file);
        });

        const uploadResponse = await api.post('/upload', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        if (uploadResponse.data.success) {
          uploadedFiles = uploadResponse.data.data.files.map((file: any, index: number) => ({
            id: Date.now() + index,
            name: file.originalName,
            filename: file.filename,
            url: file.url,
            size: file.size,
            mimetype: file.mimetype,
            type: newAttachments[index].type,
            category: newAttachments[index].category,
          }));
        }
      }

      // Manter apenas os anexos existentes que ainda estão na lista (não foram removidos pelo usuário)
      // e adicionar os novos arquivos enviados
      const existingAttachments = data.attachments
        .filter(att => !(att.file instanceof File))
        .map(att => ({
          id: att.id,
          name: att.name,
          filename: att.name,
          url: att.url?.replace(getBackendUrl(''), ''), // Remover o prefixo da URL para enviar apenas o path
          size: att.size,
          mimetype: att.type === 'image' ? 'image/jpeg' : 'application/pdf', // Assumir tipos básicos
          type: att.type,
          category: att.category,
        }));
      const allAttachments = [...existingAttachments, ...uploadedFiles];

      // Atualizar o ativo com todos os anexos
      const assetData = {
        ...data,
        currentValue: data.currentValue || null,
        attachments: allAttachments,
      };

      const response = await api.put(`/assets/${id}`, assetData);
      return response.data;
    },
    {
      onSuccess: () => {
        // Não invalidar queries para evitar refetch automático
        // queryClient.invalidateQueries(['asset', id]);
        queryClient.invalidateQueries(['assets']);
        navigate('/assets');
      },
      onError: (error: any) => {
        console.error('Erro ao atualizar ativo:', error);
        setErrors({ submit: 'Erro ao atualizar ativo. Tente novamente.' });
      },
    }
  );

  const handleInputChange = (field: keyof AssetFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));

    // Limpar erro do campo quando o usuário começar a digitar
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  // Função específica para lidar com campos de valor monetário
  const handleCurrencyChange = (
    field: 'acquisitionValue' | 'currentValue' | 'amountPaid',
    value: string
  ) => {
    const maskedValue = applyCurrencyMask(value);
    const numericValue = parseCurrency(maskedValue);

    if (field === 'acquisitionValue') {
      setAcquisitionValueDisplay(maskedValue);
    } else if (field === 'amountPaid') {
      setAmountPaidDisplay(maskedValue);
    } else {
      setCurrentValueDisplay(maskedValue);
    }

    setFormData(prev => ({
      ...prev,
      [field]: numericValue,
    }));

    // Limpar erro do campo quando o usuário começar a digitar
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const handleDetailsChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      details: {
        ...prev.details,
        [field]: value,
      },
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.description.trim()) {
      newErrors.description = 'Descrição é obrigatória';
    }

    if (formData.acquisitionValue <= 0) {
      newErrors.acquisitionValue = 'Valor de aquisição deve ser maior que zero';
    }

    if (formData.currentValue <= 0) {
      newErrors.currentValue = 'Valor atual deve ser maior que zero';
    }

    if (!formData.acquisitionDate) {
      newErrors.acquisitionDate = 'Data de aquisição é obrigatória';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    updateAssetMutation.mutate(formData);
  };

  const renderTypeSpecificFields = () => {
    switch (formData.type) {
      case 'real_estate':
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-2">
                Endereço
              </label>
              <input
                id="address"
                type="text"
                value={formData.details.address || ''}
                onChange={e => handleDetailsChange('address', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Rua, número, bairro, cidade"
              />
            </div>

            <div>
              <label htmlFor="area" className="block text-sm font-medium text-gray-700 mb-2">
                Área (m²)
              </label>
              <input
                id="area"
                type="number"
                value={formData.details.area || ''}
                onChange={e => handleDetailsChange('area', parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="0"
                min="0"
                step="0.01"
              />
            </div>

            <div>
              <label
                htmlFor="registrationNumber"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Número de Registro
              </label>
              <input
                id="registrationNumber"
                type="text"
                value={formData.details.registrationNumber || ''}
                onChange={e => handleDetailsChange('registrationNumber', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Matrícula do imóvel"
              />
            </div>

            <div>
              <label htmlFor="rooms" className="block text-sm font-medium text-gray-700 mb-2">
                Quartos
              </label>
              <input
                id="rooms"
                type="number"
                value={formData.details.rooms || ''}
                onChange={e => handleDetailsChange('rooms', parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="0"
                min="0"
              />
            </div>

            <div>
              <label htmlFor="bathrooms" className="block text-sm font-medium text-gray-700 mb-2">
                Banheiros
              </label>
              <input
                id="bathrooms"
                type="number"
                value={formData.details.bathrooms || ''}
                onChange={e => handleDetailsChange('bathrooms', parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="0"
                min="0"
              />
            </div>

            <div className="flex items-center space-x-6">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.details.garage || false}
                  onChange={e => handleDetailsChange('garage', e.target.checked)}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-700">Garagem</span>
              </label>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.details.furnished || false}
                  onChange={e => handleDetailsChange('furnished', e.target.checked)}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-700">Mobiliado</span>
              </label>
            </div>
          </div>
        );

      case 'vehicle':
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="make" className="block text-sm font-medium text-gray-700 mb-2">
                Marca
              </label>
              <input
                id="make"
                type="text"
                value={formData.details.make || ''}
                onChange={e => handleDetailsChange('make', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Ex: Toyota, Honda, Ford"
              />
            </div>

            <div>
              <label htmlFor="model" className="block text-sm font-medium text-gray-700 mb-2">
                Modelo
              </label>
              <input
                id="model"
                type="text"
                value={formData.details.model || ''}
                onChange={e => handleDetailsChange('model', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Ex: Corolla, Civic, Focus"
              />
            </div>

            <div>
              <label htmlFor="year" className="block text-sm font-medium text-gray-700 mb-2">
                Ano
              </label>
              <input
                id="year"
                type="number"
                value={formData.details.year || ''}
                onChange={e => handleDetailsChange('year', parseInt(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="2023"
                min="1900"
                max={new Date().getFullYear() + 1}
              />
            </div>

            <div>
              <label
                htmlFor="licensePlate"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Placa
              </label>
              <input
                id="licensePlate"
                type="text"
                value={formData.details.licensePlate || ''}
                onChange={e => handleDetailsChange('licensePlate', e.target.value.toUpperCase())}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="ABC-1234"
              />
            </div>

            <div>
              <label htmlFor="color" className="block text-sm font-medium text-gray-700 mb-2">
                Cor
              </label>
              <input
                id="color"
                type="text"
                value={formData.details.color || ''}
                onChange={e => handleDetailsChange('color', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Ex: Branco, Preto, Prata"
              />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (isLoadingAsset) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

    return (
    <FormPage
      backTo="/assets"
      backLabel="Voltar aos bens"
      breadcrumb="Patrimônio / Bens / Editar"
      title="Editar bem"
      subtitle="Atualize os dados do bem"
    >
        <form id="edit-asset-form" onSubmit={handleSubmit} className="space-y-8">
          {/* Informações Básicas */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center mb-6">
              <CubeIcon className="h-6 w-6 text-primary-600 mr-2" />
              <h2 className="text-xl font-semibold text-gray-900">Informações Básicas</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label
                  htmlFor="description"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Descrição do Ativo *
                </label>
                <input
                  id="description"
                  type="text"
                  value={formData.description}
                  onChange={e => handleInputChange('description', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                    errors.description ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Ex: Apartamento Centro, Casa Praia, etc."
                />
                {errors.description && (
                  <p className="mt-1 text-sm text-red-600">{errors.description}</p>
                )}
              </div>

              <div>
                <label htmlFor="assetType" className="block text-sm font-medium text-gray-700 mb-2">
                  Tipo de Ativo *
                </label>
                <select
                  id="assetType"
                  value={formData.type}
                  onChange={e => handleInputChange('type', e.target.value as any)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="real_estate">Imóvel</option>
                  <option value="vehicle">Veículo</option>
                  <option value="other">Outros</option>
                </select>
              </div>

              <div>
                <label
                  htmlFor="acquisitionDate"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Data de Aquisição *
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <CalendarIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="acquisitionDate"
                    type="date"
                    value={formData.acquisitionDate}
                    onChange={e => handleInputChange('acquisitionDate', e.target.value)}
                    className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      errors.acquisitionDate ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                </div>
                {errors.acquisitionDate && (
                  <p className="mt-1 text-sm text-red-600">{errors.acquisitionDate}</p>
                )}
              </div>

              <div>
                <label
                  htmlFor="acquisitionValue"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Valor de Aquisição *
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <CurrencyDollarIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="acquisitionValue"
                    type="text"
                    value={acquisitionValueDisplay}
                    onChange={e => handleCurrencyChange('acquisitionValue', e.target.value)}
                    className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      errors.acquisitionValue ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="0,00"
                  />
                </div>
                {errors.acquisitionValue && (
                  <p className="mt-1 text-sm text-red-600">{errors.acquisitionValue}</p>
                )}
              </div>

              <div>
                <label
                  htmlFor="amountPaid"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Valor Pago (Investido) *
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <CurrencyDollarIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="amountPaid"
                    type="text"
                    value={amountPaidDisplay}
                    onChange={e => handleCurrencyChange('amountPaid', e.target.value)}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="0,00"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Quanto já foi efetivamente pago pelo investimento.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Valor Pendente
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <CurrencyDollarIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    value={formatCurrency(Math.max(0, formData.acquisitionValue - formData.amountPaid))}
                    disabled
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Calculado automaticamente (Aquisição - Pago).
                </p>
              </div>

              <div>
                <label
                  htmlFor="currentValue"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Valor Atual *
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <CurrencyDollarIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="currentValue"
                    type="text"
                    value={currentValueDisplay}
                    onChange={e => handleCurrencyChange('currentValue', e.target.value)}
                    className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      errors.currentValue ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="0,00"
                  />
                </div>
                {errors.currentValue && (
                  <p className="mt-1 text-sm text-red-600">{errors.currentValue}</p>
                )}
              </div>
            </div>
          </div>

          {/* Detalhes Específicos */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            {renderTypeSpecificFields()}

            <div className="mt-6">
              <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-2">
                Observações
              </label>
              <textarea
                id="notes"
                value={formData.details.notes || ''}
                onChange={e => handleDetailsChange('notes', e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Informações adicionais sobre o ativo..."
              />
            </div>
          </div>

          {/* Anexos - Fotos e Documentos */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center mb-4">
              <DocumentTextIcon className="h-6 w-6 text-primary-600 mr-2" />
              <h3 className="text-lg font-semibold text-gray-900">Fotos e Documentos</h3>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Adicione fotos do imóvel e documentos como contrato, escritura, projetos de engenharia
              e arquitetura.
            </p>

            <FileUpload
              files={formData.attachments}
              onFilesChange={files => setFormData(prev => ({ ...prev, attachments: files }))}
              maxFiles={20}
              maxSizePerFile={10}
              showCategories={true}
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate('/assets')}
              className="btn btn-secondary"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={updateAssetMutation.isLoading}
              className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
            >
              {updateAssetMutation.isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Atualizando...
                </>
              ) : (
                'Atualizar Ativo'
              )}
            </button>
          </div>
        </form>
          </FormPage>
  );
};

export default EditAsset;
